import { useQueryClient } from '@tanstack/react-query';
import type { Row } from '@tanstack/react-table';
import { useMemo } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { IDataTableNormalizedResponse, TDataTableQueryKey, TDataTableRowTempData, TDataTableTempData } from '../shared/types';

interface IUseDataTableUtilityFunctionsOptions {
  queryKey: TDataTableQueryKey;
  tableTempData: TDataTableTempData;
  setTableTempData: Dispatch<SetStateAction<this['tableTempData']>>;

  rowTempData: TDataTableRowTempData;
  setRowTempData: Dispatch<SetStateAction<this['rowTempData']>>;
}

/**
 * @warning
 * Bu hook sadece DataTableInner içinde kullanılmaktadır.
 * Bu fonksiyonları kullanmak isterseniz table.options.meta ile kullanım sağlayabilirsiniz.
 */
export default function useDataTableUtilityFunctions(options: IUseDataTableUtilityFunctionsOptions) {
  const { queryKey, tableTempData, setTableTempData, rowTempData, setRowTempData } = options;

  const queryClient = useQueryClient();

  /**
   * Tablo utility fonksiyonları.
   * - table.options.meta üzerinden cell renderer vb. içinde kullanılır.
   * - invalidateDataTableQuery aynı referans olarak ref'e de verilir (DataTableInner); böylece üst bileşen de invalidate edebilir.
   */
  const tableUtilityFunctions = useMemo(() => {
    return {
      /** React Query query'i geçersiz kılar (ref ve meta üzerinden tek implementasyon). */
      invalidateDataTableQuery: () => {
        queryClient.invalidateQueries({
          queryKey,
          refetchType: 'all',
          type: 'all',
        });
      },

      /**
       * Belirtilen koşula uyan satırları tablodan kaldırır.
       * Bu işlem sadece client-side cache'i günceller, sunucuya istek göndermez.
       * Kullanım: Silme işlemi yapıldıktan sonra UI'ı güncellemek için.
       *
       * @param selector - Satırı seçmek için kullanılan fonksiyon. true döndüren satırlar silinir.
       *
       * @example
       * ```tsx
       * // ID'si 123 olan satırı sil
       * table.options.meta?.removeRow((row) => row.original.id === 123);
       *
       * // Birden fazla satırı sil (örnek: seçili satırlar)
       * const selectedIds = [1, 2, 3];
       * table.options.meta?.removeRow((row) => selectedIds.includes(row.original.id));
       * ```
       */
      removeRow: (selector: (row: Row<any>) => boolean) => {
        queryClient.setQueryData(queryKey, (_oldData: any) => {
          const oldData: IDataTableNormalizedResponse<any> = _oldData;
          const newItems = oldData?.items?.filter((row: any) => !selector(row)) || [];

          return {
            ...oldData,
            items: newItems,
            totalCount: newItems.length,
            itemCount: newItems.length,
            pageCount: 1,
            currentPage: 1,
          };
        });
      },

      /**
       * Tablo seviyesinde geçici veri saklar.
       * Bu veriler component unmount olana kadar saklanır ve tüm satırlardan erişilebilir.
       * Kullanım: Seçili öğeler, form durumları, UI state'leri gibi verileri saklamak için.
       *
       * @param data - Saklanacak veri objesi. Key-value çiftleri.
       * @param opts - Seçenekler
       * @param opts.merge - true ise mevcut veriyle birleştirir (spread), false ise tamamen değiştirir.
       *                     Varsayılan: true
       *
       * @example
       * ```tsx
       * // Seçili ID'leri sakla
       * table.options.meta?.setTableTempData({ selectedIds: [1, 2, 3] });
       *
       * // Mevcut veriye ekleme yap
       * table.options.meta?.setTableTempData({ newKey: 'value' }, { merge: true });
       *
       * // Tüm veriyi değiştir
       * table.options.meta?.setTableTempData({ onlyKey: 'onlyValue' }, { merge: false });
       * ```
       */
      setTableTempData: (data: Record<string | number, any>, opts?: { merge?: boolean }) => {
        const { merge = true } = opts || {};

        setTableTempData((prev: Record<string | number, any>) => {
          return merge ? { ...prev, ...data } : data;
        });
      },

      /**
       * Tablo seviyesinde saklanan geçici veriyi getirir.
       *
       * @param fallback - Veri yoksa veya null ise döndürülecek varsayılan değer.
       *
       * @returns Saklanan veri objesi veya fallback değeri
       *
       * @example
       * ```tsx
       * // Seçili ID'leri al
       * const selectedIds = table.options.meta?.getTableTempData({ selectedIds: [] })?.selectedIds || [];
       *
       * // Varsayılan değerle birlikte
       * const config = table.options.meta?.getTableTempData({ theme: 'light' });
       * ```
       */
      getTableTempData: (fallback?: any) => {
        return tableTempData || fallback || null;
      },

      /**
       * Tablo seviyesinde saklanan tüm geçici veriyi temizler.
       *
       * @example
       * ```tsx
       * // Form reset edildiğinde
       * table.options.meta?.clearTableTempData();
       * ```
       */
      clearTableTempData: () => {
        setTableTempData({});
      },

      /**
       * Belirli bir satır için geçici veri saklar.
       * Her satır kendi verisini saklar. Satır ID'si (row.original.id veya row.id) key olarak kullanılır.
       * Kullanım: Satır seviyesinde edit durumu, seçim durumu, form verileri gibi.
       *
       * @param row - Veri saklanacak satır. Row tipinde olmalı.
       * @param data - Saklanacak veri. Herhangi bir tip olabilir (object, string, number, boolean vb.)
       *
       * @example
       * ```tsx
       * // Satırın edit modunda olduğunu işaretle
       * table.options.meta?.setRowTempData(row, { isEditing: true, originalData: row.original });
       *
       * // Satır için form verilerini sakla
       * table.options.meta?.setRowTempData(row, { formData: { name: 'New Name' } });
       * ```
       */
      setRowTempData: (row: Row<any>, data: any) => {
        const id = row?.original?.id || row?.id;
        setRowTempData((prev) => ({ ...prev, [id]: data }));
      },

      /**
       * Belirli bir satır için saklanan geçici veriyi getirir.
       *
       * @param row - Veri getirilecek satır
       * @param fallback - Veri yoksa döndürülecek varsayılan değer
       *
       * @returns Satır için saklanan veri veya fallback değeri
       *
       * @example
       * ```tsx
       * // Satırın edit durumunu kontrol et
       * const rowData = table.options.meta?.getRowTempData(row, { isEditing: false });
       * if (rowData.isEditing) {
       *   // Edit modunda göster
       * }
       *
       * // Form verilerini al
       * const formData = table.options.meta?.getRowTempData(row)?.formData;
       * ```
       */
      getRowTempData: (row: Row<any>, fallback?: any) => {
        const id = row?.original?.id || row?.id;
        return rowTempData?.[id] || fallback || null;
      },

      /**
       * Belirli bir satır için saklanan geçici veriyi siler.
       *
       * @param row - Veri silinecek satır
       *
       * @example
       * ```tsx
       * // Edit modundan çıkıldığında
       * table.options.meta?.deleteRowTempData(row);
       * ```
       */
      deleteRowTempData: (row: Row<any>) => {
        setRowTempData((prev) => {
          const id = row?.original?.id || row?.id;
          const { [id]: _, ...rest } = prev;
          return rest;
        });
      },

      /**
       * Tüm satırlar için saklanan geçici verileri temizler.
       *
       * @example
       * ```tsx
       * // Tüm edit modlarını kapat
       * table.options.meta?.clearAllRowTempData();
       * ```
       */
      clearAllRowTempData: () => {
        setRowTempData({});
      },
    };
  }, [rowTempData, tableTempData, queryKey, queryClient, setTableTempData, setRowTempData]);

  return tableUtilityFunctions;
}
